import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { useMedia } from 'react-media-match';
import { useIntl, defineMessages } from 'react-intl';
import Modal from '@empiriecom/module-components/Modal';
import Scroll from '@empiriecom/module-components/Scroll';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { FragmentConfiguration } from '@/config';
import useDebounce from '@empiriecom/module-components/hooks/useDebounce';
import { CalculatorElement } from './InstallmentCalculatorElement';
import { useCollapsibleContext } from '../Card/CollapsibleContext';
import InstallmentCalculatorVariant from './InstallmentCalculatorVariant';
import { useInstallmentCalculatorContext } from './InstallmentCalculatorContext';
import InstallmentCalculatorElementSkeleton from './InstallmentCalculatorElement/Skeleton';

const messages = defineMessages({
  modalHeadline: {
    defaultMessage: 'Ratenrechner',
    id: 'installment-calculator.modal-headline',
  },
});

export interface InstallmentCalculatorProps {
  /** Content to be rendered for the inline component. */
  inlineContent?: JSX.Element | string;
  /**
   * Flag if inline element should be used.
   * @default false
   */
  isInline?: boolean;
  /** Flag indicating if the price should be editable.
   * @default false
   */
  isPriceEditable?: boolean;
  /**
   * The request source.
   * @default "pdv"
   */
  source?: string;
}

/**
 * <p>This is the installment calculator component. It can either be rendered as a preview box or as an inline component.</p>
 * <p>On initial load the preview box calls the installment calculator API with the given price and returns the calculated installment values.
 * If a valid response is returned the value of a single installment rate as well as the amount of rates will be displayed.</p>
 * <p>If the inline component is used the content to be rendered has to be passed via the parameter `content` and can either be
 * of type `JSX.Element` or `string`.</p>
 * <p>By clicking on the component a modalbox opens which enables the user to configure various values like e.g. the amount of installment rates.
 * The modalbox also provides a configurable option if the price should be editable or not. If a value changes the installment
 * calculator API will be called again and the values will be updated according to the newly calculated values returned from the API call.</p>
 */
export const InstallmentCalculator: FunctionComponent<InstallmentCalculatorProps> = ({
  inlineContent,
  isInline = false,
  isPriceEditable = false,
  source = 'pdv',
}): ReactElement => {
  const mobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const [isShowModal, setIsShowModal] = useState<boolean>(false);
  const intl = useIntl();
  const { updateCollapsible } = useCollapsibleContext();
  const {
    error,
    isLoading,
    response,
    setIsPriceEditable,
    setInstallmentRequestSource,
  } = useInstallmentCalculatorContext();
  const {
    installmentCalculator: { cardIdentifier, cardLayout },
  } = useConfig<FragmentConfiguration>();
  const isDebouncedLoading = useDebounce(isLoading, 500);
  const modalProps = {
    large: !mobile,
  };

  useEffect(() => {
    setIsPriceEditable(isPriceEditable);
    setInstallmentRequestSource(source);
  }, []);

  return (
    <>
      {/* render nothing if an error occured. */}
      {(error || (response && response.error)) && (
        <span data-testid="installment-calculator-error" data-error={response && response.error} />
      )}
      {isDebouncedLoading && <InstallmentCalculatorElementSkeleton />}
      {response && !response.error && (
        <div data-testid="installment-calculator">
          {cardLayout ? (
            <Scroll
              duration={500}
              render={(scroll) => (
                <CalculatorElement
                  inlineContent={inlineContent}
                  isInline={isInline}
                  onClick={() => {
                    scroll();
                    updateCollapsible({
                      collapsed: false,
                      identifier: cardIdentifier,
                    });
                  }}
                />
              )}
              positionAfterScroll={`#${cardIdentifier}`}
            />
          ) : (
            <>
              <CalculatorElement
                inlineContent={inlineContent}
                isInline={isInline}
                onClick={() => setIsShowModal(true)}
              />
              <Modal
                // modalprops can't be passed in an other way
                // eslint-disable-next-line
                {...modalProps}
                noContentPadding
                onRequestClose={() => setIsShowModal(false)}
                open={isShowModal}
                headline={intl.formatMessage(messages.modalHeadline)}
              >
                <InstallmentCalculatorVariant />
              </Modal>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default InstallmentCalculator;
